<template>
  <section class="h-100">
    <div class="mb-2 d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-end vendors--filters">
        <b-form-group label="Vendor" class="mr-2">
          <b-form-input v-model="vendorQuery" @input="onSearch"></b-form-input>
        </b-form-group>

        <b-form-group label="Vendor Types" class="mr-2">
          <b-form-select v-model="vendorType" @change="onSearch">
            <option :value="null"></option>
            <option v-for="(selectOption, indexOpt) in sortSelect(vendorTypes)" :key="indexOpt" :value="selectOption.id">
              {{ selectOption.name }}</option>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Attribute" class="mr-2">
          <b-form-select v-model="vendorAttribute" @change="onSearch">
            <option :value="null"></option>
            <option
              v-for="(attribute) in sortSelect(sortedAttributes,'attributeName')"
              v-bind:key="attribute.id"
              v-bind:value="attribute.attributeId"
            >{{attribute.attributeName}}</option>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Manufacturer" class="mr-2">
          <b-form-select v-model="selectedManufacturerId" @change="onSearch">
            <option :value="null"></option>
            <option
              v-for="manufacturer in sortSelect(manufacturerOptions)"
              v-bind:key="manufacturer.id"
              v-bind:value="manufacturer.id"
            >{{manufacturer.name}}</option>
          </b-form-select>
        </b-form-group>
        <b-form-group class="mr-3">
          <b-form-checkbox v-model="isShowAvl" @input="onSearch" switch>
            Show AVL
          </b-form-checkbox>     
        </b-form-group>
        <b-form-group class="mr-3">
          <b-form-checkbox v-model="isShowNonAvl" @input="onSearch" switch>
            Show Non-AVL
          </b-form-checkbox>     
        </b-form-group>
        <b-button size="sm" @click="onClear">Clear filters</b-button>
      </div>

      

      <edit-company :isNewVendor="true" :id="''" @currentCompany="onCurrentIdChange"></edit-company>
      <!-- <b-button size="sm" variant="primary" @click="showModal">
        <i class="fa fa-plus" />
        Add new
      </b-button> -->
      <b-modal
        id="add-vendor-modal"
        ref="add-vendor-modal"
        size="md"
        ok-title="Add"
        @ok="handleSubmitVendor"
        @hidden="resetForm"
      >
        <template slot="modal-header">
          <h5 class="modal-title">Add vendor</h5>
        </template>

        <form id="add-vendor-form">
          <b-form-group class="required" label="Name">
            <b-form-input
              :class="{ 'is-invalid': $v.name.$error }"
              @blur="$v.name.$touch()"
              v-model.trim="name"
            ></b-form-input>

            <div v-if="!$v.name.required" class="invalid-feedback">Company name is required</div>
          </b-form-group>
        </form>
        <required-descriptor />
      </b-modal>
    </div>
    <grid-component
        isResizable
        isReorderable
        isSortable
        :gridId="gridId"
        :pageable="pageable"
        :skip="pageInfo.pageSize * (pageInfo.pageNumber - 1)"
        :take="pageInfo.pageSize"
        :itemData="itemData"
        :pageSizes="pageInfo.pageSize"
        :totalItems="pageInfo.totalItems"
        :fields="fields"
        :sortBy="pageInfo.sortBy"
        :sortOrder="pageInfo.sortOrder"
        :sortMethod="sortVendors"
        :checkBoxValues="checkedRowItemIds"
        :styleCustom="'height: 250px'"
        @getData="onGetData"
        @checkedItem="checkedRowItemIds=$event"
      />
      <div class="text-right">
        <b-button size="sm" variant="primary" @click="handleAddVendors" :disabled="isSaving || selectedIds.length == 0">
          <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>
          <i class="fa fa-plus" />Add selected
        </b-button>
      </div>
      
  </section>

</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import debounce from 'lodash/debounce';
import orderBy from 'lodash/orderBy';
import { required } from 'vuelidate/lib/validators';

import GridSettings from '@/shared/components/GridSettings';
import GridComponent from '@/shared/components/GridComponent';
import { GRID_ID, SEARCH_DEBOUNCE_TIME, GRID_PAGE_SIZES } from '@/shared/constants';
import { prepareColumnsData, sortChange } from '@/shared/helpers';

import fields from './fields';

import EditCompany from '@/pages/Company/components/EditCompany';

const initialFilters = {
  vendorQuery: '',
  vendorType: null,
  vendorAttribute: null,
  selectedManufacturerId: null,
};

export default {
  components: {
    EditCompany,
    GridSettings,
    GridComponent
  },
  data: () => ({
    prepareColumnsData,
    name: '',
    fields,
    ...initialFilters,
    selectedVendorId: '',
    gridId: GRID_ID.RFP_GRID_VENDORS,
    checkedRowItemIds: [],
    pageable: {
      pageSizes: GRID_PAGE_SIZES,
    },
    isSaving:false,
    vendorTypes: [],
    isShowAvl: true,
    isShowNonAvl: true,
  }),
  computed: {
    ...mapGetters('rfpDetails', ['partsById']),
    ...mapGetters('lookup', ['manufacturers']),
    ...mapGetters('attributes', ['vendorAttributes']),
    ...mapGetters('rfpVendors', ['vendors', 'pageInfo', 'filters']),
    ...mapGetters('account', ['gridSettingsById']),
    gridSettings() {
      return this.gridSettingsById(GRID_ID.RFP_DETAILS_VENDORS);
    },
    selectedIds() {
      return this.vendors
        .filter(vendor => this.checkedRowItemIds.includes(vendor.id))
        .map((vendor) => (
          { 'vendorId': vendor.companyId, 'contactId': vendor.id }));
    },
    rfpId() {
      return this.$route.params.id;
    },
    sortedAttributes() {
      return orderBy(this.vendorAttributes, ['attributeName'], ['asc']);
    },
    itemData() {
      if (this.vendors == undefined || !this.vendors.length) return [];
      return this.vendors;
    },
    parts() {
      return this.partsById(this.rfpId);
    },
    manufacturerOptions() {
      return this.manufacturers.filter(m => this.parts.map(p => p.mfgName).includes(m.name))
                          .map(function(mfg){
                            return {
                              id: mfg.id,
                              name: mfg.name
                            };
                          });
    }
  },
  watch: {

  },
  methods: {
    ...mapActions('rfpVendors', [
        'getVendors',
        'addVendors',
        'createVendor',
        'getAllVendors',
        'sortVendors',
        'searchVendors'
      ]),
    ...mapActions('account', ['saveGridSettings']),
    ...mapActions('rfpSelectedVendors', ['getSelectedVendors']),
    ...mapActions('lookup', ['getLookupValueById']),
    ...mapMutations('rfpVendors', {rfpVendorSetItem: 'setItem'}),
    async onGetData(params) {
      await this.getVendors(params);
    },
    selectedItem: function(val) {
      //console.log(val,'=val');
    },
    async onCurrentIdChange (val) {
      await this.addVendors({ rfpId: this.rfpId, vendors: [{ vendorId: val.id, contactId: 0 }] });
      await this.getSelectedVendors(this.rfpId);
    },
    onRowClick: function({ id }) {
      const vendorIndex = this.vendors.findIndex(vendor => vendor.id === id);
      this.vendors[vendorIndex].selected = !this.vendors[vendorIndex].selected;
    },
    onClear: async function() {
      Object.keys(initialFilters).forEach(key => {
        this[key] = initialFilters[key];
      });

      this.rfpVendorSetItem({ resource: 'filters', payload: initialFilters });

      await this.getVendors({});
    },
    onSearch: debounce(function() {
      this.searchVendors({
        name: this.vendorQuery,
        vendorType: this.vendorType,
        attributeId: this.vendorAttribute,
        // AccountTypeId: this.vendorType,
        MfrId: this.selectedManufacturerId,
        IsShowAvl: this.isShowAvl,
        isShowNonAvl: this.isShowNonAvl
      });
    }, SEARCH_DEBOUNCE_TIME),
    async handleAddVendors() {
      try {
        this.isSaving = true;
        await this.addVendors({
          rfpId: this.rfpId,
          vendors: this.selectedIds,
        });
        this.isSaving = false;
        await this.getSelectedVendors(this.rfpId);
      }catch (e) {
        this.isSaving = false;
      }
    },
    showModal() {
      this.$root.$emit('bv::show::modal', 'add-vendor-modal', '#btnShow');
    },
    async handleSubmitVendor(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$v.name.$touch();
      if (!this.$v.name.$error) {
        await this.createVendor(this.name);
        this.$nextTick(() => {
          this.$refs['add-vendor-modal'].hide()
        });
      }
    },
    resetForm() {
      this.name = '';
      this.$v.$reset();
    },
  },
  validations: {
    name: { required },
  },
  async created() {
    this.rfpVendorSetItem({ resource: 'pageInfo', payload: {...this.pageInfo, ...this.gridSettings.sort, ...this.gridSettings.pageSize} });
    await this.getVendors({});

    await this.getLookupValueById(55).then(result => {
      this.vendorTypes = result;
    });
  },
};
</script>

<style lang="scss" scoped>
.vendors--filters {
  .form-group {
    margin-bottom: 0;
  }

  & > * {
    margin-right: 0.5rem;
  }
}

.grid {
  max-height: 100px;
  height: auto !important;
  margin-bottom: 10px;
}
</style>
